import { 
    useReducer, 
    useEffect,
} from 'react';

import formReducer, { actions } from './reducer';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import set from 'lodash/set';

const getCaller = () => {
    const stackTrace = new Error().stack;
    const stackSplit = get(stackTrace.split('\n'), '[3]', '').trim();
    const caller = get(stackSplit.split(' '), '[1]', '');
    return caller;
};


const useForm = ({
    initialState, 
    props, 
    onLoad,
    onSubmit,
    debug = false,
    //stateType = 'form',
    stateToTriggerOnLoad = [],
}) => {
    //Check if initialState is an object with keys
    if (!initialState || Object.keys(initialState).length === 0) 
        throw new Error('Must supply initialState');

    /**
     * Restructures the initialState to handle the common 
     * form implementation for managing values+errors 
     * */
    initialState = {
        values: Object.keys(initialState).reduce((prev, curr) => {
            let val = initialState[curr];
            if (val === null || val === undefined) val = '';
            prev[curr] = val;
            return prev;
        }, {})
        ,
        errors: {},
        isValid: null
    };


    const [state, dispatch] = useReducer(formReducer, initialState);
    const callerComponent = getCaller();
    if (debug) console.log(callerComponent, state, props);

    /**
     * Used to re-run the onLoad function to assist in refreshing the data
     */
    const onRefresh = async () => {
        if (onLoad) {
            const values = await onLoad({
                values: state.values, 
                props,
            });
            if (values) {
                if (debug) console.log(`${callerComponent} onLoad`, values);
                dispatch({type: actions.ON_LOAD, payload: {state: values}});
            }
        }
    };

    /**
     * Used to run a common onLoad function if supplied when the caller
     * component loads
     * */
    useEffect(() => {
        onRefresh();
    }, [onLoad]);

    /**
     * Used to set all form errors as well as determine if this 
     * is an API response. If a Joi schema has been used in the API 
     * response we will automatically set form labels based on what the 
     * API has provided back as a response. Otherwise, set all form errors 
     * using the errors object that we typically construct
     * */
    const onFail = (result) => {
        if (debug) console.log(`${callerComponent} onFail`, result);
        if (result.errorType == 'validationError') {
            const invalidRequestParameters = result.error.details;//.find(x=> x.code == 'INVALID_REQUEST_PARAMETER');
            if (debug) console.log(invalidRequestParameters);
            const errors = invalidRequestParameters.reduce((prev, current)=> {
                const key = current.context.key;
                const error = current.message;
                return {
                    ...prev,
                    [key]: error,
                };
            }, {});
            if (debug) console.log(errors);
            dispatch({type: actions.ON_INVALID_FORM, payload: errors});    
        } else {
            dispatch({ type: actions.ON_SUBMIT_FAIL, payload: result.error });
        }
    };

       
    /**
     * If in your onSubmit call you have validation errors you can submit 
     * your errors to this function and if you're using FormInput then 
     * you will automatically get your label error message set on the control
     * input 
     * */
    const onInvalidForm = (errors) => {
        if (debug) console.log(`${callerComponent} onInvalidForm`, errors);
        dispatch({type: actions.ON_INVALID_FORM, payload: errors});
    };

    /**
     * Resets all form errors back to nothing if called as resetFormErrors() 
     * */
    const onResetFormErrors = (errors = {}) => {
        if (debug) console.log(`${callerComponent} resetFormErrors`, errors);
        dispatch({type: actions.ON_INVALID_FORM, payload: errors});
    };

    /**
     * Change any state value to a new value as well as 
     * trigger onLoad again if the prop being changed is 
     * tracked in stateToTriggerOnLoad. Examples for when 
     * onLoad would need to be triggered again would be for 
     * components like Grid controls where pageSize, pageNum,
     * nextPage, prevPage are called. 
     * */
    const onChange = async (value, path) => {
        if (debug) console.log(`${callerComponent} onChange`, value, path);
        let newValue = value;
        if (typeof value == 'function') {
            const newState = value(state.values);
            return dispatch({type: actions.ON_LOAD, payload: {state: newState}});
        }
        if (value && value.target) {
            newValue = value.target.value;
        }
        dispatch({ type: actions.FIELD_CHANGE, payload: { key: path, value: newValue } });
        if (stateToTriggerOnLoad.includes(path)) { 
            if (onLoad) {
                let params = {
                    ...state.values,
                };
                set(params, path, newValue);
                const values = await onLoad({values: params, props});
                if (values) {
                    if (debug) console.log(`${callerComponent} onLoad`, values);
                    dispatch({type: actions.ON_LOAD, payload: {state: values}});
                }
            }
        }
    };

    /**
     * Change multiple state values at one time to prevent 
     * multiple re-renders for each field as re-call onLoad 
     * for any value tracked in stateToTriggerOnLoad. Examples 
     * for when onLoad would need to be triggered again would be for 
     * components like Grid controls where pageSize, pageNum,
     * nextPage, prevPage are called. Using this function 
     * prevents overlapping onLoad calls for one trigger. Another 
     * example would be setting pageNum = 0 and a filter.<input> 
     * at the same time would only re-trigger 1 onLoad call.
     * */
    const onChangeBulk = async (values) => {
        dispatch({
            type: actions.ON_LOAD, 
            payload: {
                state: {
                    ...state.values,
                    ...values
                }
            }});
        const valueKeys = Object.keys(values);
        if (stateToTriggerOnLoad.includes(...valueKeys)) { 
            if (onLoad) {
                const results = await onLoad({
                    values: {
                        ...state.values,
                        ...values
                    },
                    props
                });
                if (results) {
                    if (debug) console.log(`${callerComponent} onLoad`, results);
                    dispatch({type: actions.ON_LOAD, payload: {
                        state: {
                            ...results
                        }
                    }});
                }
            }
        }
    };

    const onBlur = (value, path, validator) => {
        if (!validator) return;
        const result = validator(value);
        if (result === true) {
            dispatch({ type: actions.FIELD_ERROR_REMOVE, payload: { key: path } });
        } else {
            dispatch({ type: actions.FIELD_ERROR_ADD, payload: { key: path, error: result } });
        }
    };

    /**
     * Call this function to set a label on your form for the overall 
     * error submission of a network call. Examples would include 
     * HTTP error code 400, 500, 404
     * */
    const onFormSubmitError = (error) => {
        dispatch({type: actions.ON_SUBMIT_FAIL, payload: error});
    }

    const onFormSubmit = async (cb) => {
        try {
            const result = onSubmit && await onSubmit({
                values: state.values, 
                props,
                onFail, 
                onInvalidForm, 
                onResetFormErrors,
                onChange,
            });
            if (typeof cb == 'function') cb(result);
        } catch (e) {
            onFail({error: e.message});
        }
    }

    return {
        state,
        dispatch,
        onBlur,
        onChange,
        onChangeBulk,
        onInvalidForm,
        onFormSubmit,
        onResetFormErrors,
        onRefresh,
        onFormSubmitError,
        onFail
    };
};

useForm.propTypes = {
    initialState: PropTypes.any.isRequired,
    props: PropTypes.any,
    onLoad: PropTypes.func,
    stateToTriggerOnLoad: PropTypes.array,
    onSubmit: PropTypes.func,
    debug: PropTypes.bool,
};

export default useForm;
